import { jsx, jsxs } from "react/jsx-runtime";
import {
  preventDefault,
  useContainer,
  useEditor,
  useEditorComponents,
  useValue
} from "@tldraw/editor";
import { ContextMenu as _ContextMenu } from "radix-ui";
import { memo, useCallback, useEffect, useRef } from "react";
import { useMenuIsOpen } from "../../hooks/useMenuIsOpen.mjs";
import { useDirection, useTranslation } from "../../hooks/useTranslation/useTranslation.mjs";
import { TldrawUiMenuContextProvider } from "../primitives/menus/TldrawUiMenuContext.mjs";
import { DefaultContextMenuContent } from "./DefaultContextMenuContent.mjs";
const DefaultContextMenu = memo(function DefaultContextMenu2({
  children,
  disabled = false
}) {
  const editor = useEditor();
  const msg = useTranslation();
  const { Canvas } = useEditorComponents();
  const menuCanOpen = useValue(
    "context menu can open",
    () => !editor.getInstanceState().isCoarsePointer || editor.isIn("select"),
    [editor]
  );
  const preventEscapeFromLosingShapeFocus = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        editor.getContainer().focus();
      }
    },
    [editor]
  );
  useEffect(() => {
    const body = editor.getContainerDocument().body;
    return () => {
      body.removeEventListener("keydown", preventEscapeFromLosingShapeFocus, {
        capture: true
      });
    };
  }, [editor, preventEscapeFromLosingShapeFocus]);
  const suppressDismissUntilRef = useRef(0);
  const cb = useCallback(
    (isOpen2) => {
      const body = editor.getContainerDocument().body;
      if (!isOpen2) {
        const onlySelectedShape = editor.getOnlySelectedShape();
        if (onlySelectedShape && editor.isShapeOrAncestorLocked(onlySelectedShape)) {
          editor.setSelectedShapes([]);
        }
        editor.timers.requestAnimationFrame(() => {
          body.removeEventListener("keydown", preventEscapeFromLosingShapeFocus, {
            capture: true
          });
        });
      } else {
        body.addEventListener("keydown", preventEscapeFromLosingShapeFocus, {
          capture: true
        });
        if (editor.getInstanceState().isCoarsePointer) {
          suppressDismissUntilRef.current = Date.now() + 500;
          const selectedShapes = editor.getSelectedShapes();
          const currentPagePoint = editor.inputs.getCurrentPagePoint();
          const shapesAtPoint = editor.getShapesAtPoint(currentPagePoint);
          if (
            // if there are no selected shapes
            !editor.getSelectedShapes().length || // OR if none of the shapes at the point include the selected shape
            !shapesAtPoint.some((s) => selectedShapes.includes(s))
          ) {
            const lockedShapes = shapesAtPoint.filter((s) => editor.isShapeOrAncestorLocked(s));
            if (lockedShapes.length) {
              editor.select(...lockedShapes.map((s) => s.id));
            }
          }
        }
      }
    },
    [editor, preventEscapeFromLosingShapeFocus]
  );
  const container = useContainer();
  const dir = useDirection();
  const [isOpen, handleOpenChange] = useMenuIsOpen("context menu", cb);
  const content = children ?? /* @__PURE__ */ jsx(DefaultContextMenuContent, {});
  return /* @__PURE__ */ jsxs(_ContextMenu.Root, { dir, onOpenChange: handleOpenChange, modal: false, children: [
    /* @__PURE__ */ jsx(
      _ContextMenu.Trigger,
      {
        onContextMenu: menuCanOpen ? void 0 : preventDefault,
        dir: "ltr",
        disabled: disabled || !menuCanOpen,
        children: Canvas ? /* @__PURE__ */ jsx(Canvas, {}) : null
      }
    ),
    isOpen && /* @__PURE__ */ jsx(_ContextMenu.Portal, { container, children: /* @__PURE__ */ jsx(
      _ContextMenu.Content,
      {
        className: "tlui-menu tlui-scrollable",
        "data-testid": "context-menu",
        "aria-label": msg("context-menu.title"),
        alignOffset: -4,
        collisionPadding: 4,
        onContextMenu: preventDefault,
        onPointerDownOutside: (e) => {
          if (Date.now() < suppressDismissUntilRef.current) e.preventDefault();
        },
        onInteractOutside: (e) => {
          if (Date.now() < suppressDismissUntilRef.current) e.preventDefault();
        },
        onFocusOutside: (e) => {
          if (Date.now() < suppressDismissUntilRef.current) e.preventDefault();
        },
        children: /* @__PURE__ */ jsx(TldrawUiMenuContextProvider, { type: "context-menu", sourceId: "context-menu", children: content })
      }
    ) })
  ] });
});
export {
  DefaultContextMenu
};
//# sourceMappingURL=DefaultContextMenu.mjs.map
